<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import pageFlow from '@/flows';
import { compact, debounce } from 'lodash';
import conditions from './conditions';
import { PREVIEW_ACTION_TYPES, PREVIEW_API_ACTIONS_ENUM } from '@/store/modules/preview';
import { isEmptyVal } from '@/utils';
import { checkPlanDownSellForEdit } from '@/common/service/validate/downsell';
import Big from 'big.js';
import nextMixin from '@/views/config/shopping-cart/nextMixin';

const debounceDelay = 1000;

const makeUpPreviewCents = ({ amount, taxAmount, totalAmount, creditBalance, details, refundCredit, ...rest }) => {
  const mul100 = x => (new Big(x)).times(100).toNumber();
  return {
    ...rest,
    amount: mul100(amount),
    taxAmount: mul100(taxAmount),
    totalAmount: mul100(totalAmount),
    creditBalance: mul100(creditBalance),
    refundCredit: mul100(refundCredit),
    details: details.map((x) => ({
      ...x,
      chargePlusDiscountAmount: mul100(x.chargePlusDiscountAmount ?? x.chargeAmount), // before discounted charge amount
      chargeAmount: mul100(x.chargeAmount),
      discountAmount: mul100(x.discountAmount),
      tax: mul100(x.tax),
    })),
  };
};

export default {
  data() {
    return {
      isPendingDowngrade: false,
    };
  },
  mixins: [conditions, nextMixin],
  methods: {
    ...mapMutations([
      'setIsLoadingPreview',
      'setProratedCharges',
      'resetProratedCharges',
      'setPreviewType',
      'setIsProratedChargeAmountCalculated',
      'setIsProrated',
      'setIsProratedLoading',
      'setIsTaxUpdateAttempted',
    ]),
    ...mapActions({
      resetProratedCharges: PREVIEW_ACTION_TYPES.resetProratedCharges,
      setProratedCharges: PREVIEW_ACTION_TYPES.setProratedCharges,
    }),
    ...mapMutations(['setNextChargeDate']),
    loadProratedAmount: debounce(async function () {
      if (!this.is) return; // auto-add leads to multiple cart changes, so we still need to check before loading happens after the debounce
      try {
        this.setIsLoadingPreview(true);
        // Call /opc/plan/update with isPreview=true
        const { eventResult = {} } = (await pageFlow.submit({
          preview: true,
          previewActions: compact([PREVIEW_API_ACTIONS_ENUM.PRORATED, this.mx_enableServerNextChargeDateLogic ? PREVIEW_API_ACTIONS_ENUM.NEXT_CHARGE_DATE : null]),
        })) || {};
        const error = new Error('Invalid preview data');
        if (!eventResult?.previewOrder) {
          throw error;
        }
        const previewOrder = JSON.parse(eventResult?.previewOrder);
        if (!previewOrder || isEmptyVal(previewOrder)) {
          throw error;
        }
        /*
        if(previewOrder.totalAmount - previewOrder.creditBalance < 0){
          throw error;
        }
        */
        await this.setProratedCharges({ ...makeUpPreviewCents(previewOrder), __retain: true });
        if (eventResult?.nextChargeDate) {
          const nextChargeDate = JSON.parse(eventResult.nextChargeDate);
          this.setNextChargeDate(nextChargeDate);
        }
        this.setIsProratedChargeAmountCalculated(true);
      } catch (e) {
        this.setIsProrated(false); // match prorated condition though, results in business error;
        this.setIsProratedChargeAmountCalculated(false);
      } finally {
        this.setIsLoadingPreview(false);
        this.setIsProratedLoading(false);
        this.setIsTaxUpdateAttempted(true);
      }
    }, debounceDelay),
    prepare() {
      this.setIsProratedLoading(true);
      this.setIsTaxUpdateAttempted(false);
      this.setPreviewType('1');
      this.resetProratedCharges();
    },
    clear() {
      this.setIsProratedLoading(false);
      this.setIsLoadingPreview(false);
      this.setIsProratedChargeAmountCalculated(false);
      this.setPreviewType('');
      this.resetProratedCharges();
    },
  },
  computed: {
    ...mapGetters(['cartFingerPrint', 'isfraudRiskOperation', 'isSelectedSameAsEditablePlansInBought', 'getSelectedPlans', 'couponFingerPrint']),
    is() {
      if (!this.getSelectedPlans.length) return false; // block any problematic flows
      // preconditions for prorated pricing
      if (this.isSelectedSameAsEditablePlansInBought) return false;
      if (this.isPendingDowngrade) return false;
      if (this.isfraudRiskOperation) return false;
      if (this.condition_ZOOM_786892) return true;
      return false;
    },
    indicator() {
      // there are async validations such as coupon post check @Arvin Cheng
      return `${this.cartFingerPrint}&${this.is}&${this.couponFingerPrint}`;
    },
  },
  watch: {
    is: {
      immediate: true,
      handler(is) {
        this.setIsProrated(is);
        if (!is) {
          this.clear();
        }
      },
    },
    indicator: {
      immediate: true,
      async handler() {
        if (this.$context.is.edit) {
          try {
            await checkPlanDownSellForEdit(true);
            this.isPendingDowngrade = false;
          } catch (e) {
            this.isPendingDowngrade = true;
          }
        }
        if (this.is) {
          this.setIsProrated(true);
          this.prepare();
          this.loadProratedAmount();
        }
      },
    },
    // block negative tax display in license increase-only scenario
    '$store.getters.getProratedChargeTaxAmount': {
      immediate: true,
      handler(val) {
        if (val < 0 && this.mx_isSingleIncreaseLicense && !this.mx_prorated4SupportedCouponCase) {
          this.setIsProrated(false);
        }
      },
    },
  },
  mounted(){
    this.$nextTick(()=>{
      if(this.is){
        window['optimizely'].push({
          type: 'event',
          eventName: 'opc__is-prorated-charge-amount__initial'
        });
      }
    })
  }
};
</script>
